import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBug, faFeather, faLightbulb, faHandsHelping, faHeart } from "@fortawesome/free-solid-svg-icons";
import NewUsersChart from "../analytics/NewUsersChart";
import NewTasksChart from "../analytics/NewTasksChart";
import { fetchChats } from "../feedbacks/api";

const Dashboard = () => {
  const [recentChats, setRecentChats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecentChats = async () => {
      setLoading(true);
      const data = await fetchChats(1, 5);
      setRecentChats(data?.chats || []);
      setLoading(false);
    };
    fetchRecentChats();
  }, []);

  const formatDate = (date) => {
    const now = new Date();
    const messageDate = new Date(date);
    messageDate.setHours(messageDate.getHours() + 1);
    const diffInHours = (now - messageDate) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return messageDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return messageDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'bug': return faBug;
      case 'feature': return faFeather;
      case 'improvement': return faLightbulb;
      case 'assistance': return faHandsHelping;
      case 'appreciation': return faHeart;
      default: return faBug;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'bug':
      case 'feature':
      case 'improvement': return '#3273dc';
      case 'assistance': return '#ffb700';
      case 'appreciation': return '#00d1b2';
      default: return '#3273dc';
    }
  };

  return (
    <div className="container mt-4 fade-in">
      <div className="neumorphic p-4 mb-4">
        <h1 className="title is-4 mb-0" style={{ color: 'var(--text-primary)' }}>Dashboard Overview</h1>
      </div>

      <div className="columns is-multiline">
        <div className="column is-6-desktop is-12-tablet">
          <div className="neumorphic p-4 h-100">
            <NewUsersChart />
          </div>
        </div>

        <div className="column is-6-desktop is-12-tablet">
          <div className="neumorphic p-4 h-100">
            <NewTasksChart />
          </div>
        </div>

        <div className="column is-12">
          <div className="neumorphic p-4">
            <Link to="/chats" className="title is-4 mb-4" style={{ color: 'var(--text-primary)' }}>Chats</Link>
            {loading ? (
              <div className="has-text-centered">
                <div className="loader is-loading" style={{ width: 50, height: 50 }}></div>
              </div>
            ) : (
              recentChats.map((chat) => (
                <Link to={`/chats/${chat._id}`} key={chat._id} className="chat-card-link mb-3 is-block" 
                  style={chat.status === "Closed" || chat.status === "Handled" ? { opacity: 0.45 } : {}}>
                  <div className="neumorphic p-4" style={{
                    background: 'var(--bg-secondary)',
                    opacity: chat.read ? 0.8 : 1
                  }}>
                    <div className="columns is-mobile is-vcentered">
                      <div className="column is-2-mobile is-1-tablet">
                        <div className="neumorphic-inset is-flex is-justify-content-center is-align-items-center"
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            color: 'var(--accent-primary)',
                            fontSize: '1.2em'
                          }}>
                          {chat.user.name[0].toUpperCase()}
                        </div>
                      </div>
                      <div className="column">
                        <div className="is-flex is-justify-content-space-between is-flex-wrap-wrap">
                          <div>
                            <p className="title is-5 mb-1" style={{ color: chat.is_premium ? "var(--primary)" : 'var(--text-primary)' }}>
                              {chat.user.name}
                            </p>
                            <p className="subtitle is-6 mb-2" style={{ color: 'var(--text-secondary)' }}>
                              {(chat.lastMessage?.content || chat.content)?.slice(0, 70)}
                              {(chat.lastMessage?.content || chat.content)?.length > 70 ? '...' : ''}
                            </p>
                          </div>
                          <div className="has-text-right">
                            <div className="tags mb-2">
                              {chat.category && chat.category !== "unknown" && (
                                <span className="tag is-medium neumorphic" style={{ color: getCategoryColor(chat.category) }}>
                                  <span className="icon">
                                    <FontAwesomeIcon icon={getCategoryIcon(chat.category)} />
                                  </span>
                                  <span>{chat.category}</span>
                                </span>
                              )}
                              {chat.status && (
                                <span className="tag is-medium neumorphic" style={{ color: "black" }}>
                                  {chat.status || "Open"}
                                </span>
                              )}
                            </div>
                            <p style={{ color: 'var(--text-secondary)' }} className="is-size-7">
                              {formatDate(chat.updated_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    </div>
                </Link>
              ))
            )}
            {!loading && recentChats.length === 0 && (
              <div className="has-text-centered">
                <p className="is-size-6" style={{ color: 'var(--text-secondary)' }}>No recent chats</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
